import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Search, ShoppingBag } from "lucide-react";
import { useCart } from '../../context/CartContext';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';

const navLinks = [
    { name: 'NEW DROPS', path: '/products?category=all', hot: true },
    { name: 'T-SHIRTS', path: '/products?category=tshirts' },
    { name: 'HOODIES', path: '/products?category=hoodies' },
    { name: 'CUSTOMIZE', path: '/customize' },
    { name: 'BLOG', path: '/blog' },
    { name: 'ABOUT', path: '/about' },
    { name: 'CONTACT', path: '/contact' },
];

export const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const { itemCount } = useCart();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <header 
            className={`fixed top-0 w-full z-50 transition-all duration-300 ${
                scrolled ? 'bg-black/90 backdrop-blur-xl border-b border-white/5' : 'bg-transparent'
            }`}
            data-testid="navbar"
        >
            {/* Announcement Bar */}
            <div className="bg-lime text-black overflow-hidden">
                <div className="animate-marquee whitespace-nowrap py-2 flex gap-16">
                    {[...Array(2)].map((_, i) => (
                        <React.Fragment key={i}>
                            <span className="text-xs font-bold tracking-[0.15em] uppercase">NEW DROP: CHILL SEASON VOL. 3</span>
                            <span className="text-xs opacity-50">✦</span>
                            <span className="text-xs font-bold tracking-[0.15em] uppercase">BUY 2 GET 20% OFF — CODE: CHILL20</span>
                            <span className="text-xs opacity-50">✦</span>
                            <span className="text-xs font-bold tracking-[0.15em] uppercase">FREE SHIPPING ON ORDERS ₹999+</span>
                            <span className="text-xs opacity-50">✦</span>
                            <span className="text-xs font-bold tracking-[0.15em] uppercase">EASY 15-DAY RETURNS</span>
                            <span className="text-xs opacity-50">✦</span>
                        </React.Fragment>
                    ))}
                </div>
            </div>

            {/* Main Header */}
            <div className="flex items-center justify-between px-4 md:px-8 h-16 md:h-20">
                {/* Mobile Menu */}
                <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                    <SheetTrigger asChild className="md:hidden">
                        <button className="p-2" data-testid="mobile-menu-btn">
                            <Menu className="w-6 h-6" />
                        </button>
                    </SheetTrigger>
                    <SheetContent side="left" className="w-full max-w-sm bg-background border-r border-border p-0">
                        <div className="flex flex-col h-full">
                            <div className="flex items-center justify-between p-6 border-b border-border">
                                <Link to="/" className="font-heading text-2xl" onClick={() => setMobileOpen(false)}>
                                    <span className="text-lime">YES</span>suhchill
                                </Link>
                                <button onClick={() => setMobileOpen(false)}>
                                    <X className="w-6 h-6" />
                                </button>
                            </div>
                            <nav className="flex-1 py-4">
                                {navLinks.map((link) => (
                                    <Link
                                        key={link.name}
                                        to={link.path}
                                        onClick={() => setMobileOpen(false)}
                                        className={`block px-6 py-4 font-heading text-2xl border-b border-border/50 transition-colors hover:text-lime ${
                                            link.hot ? 'text-lime' : ''
                                        }`}
                                        data-testid={`mobile-nav-${link.name.toLowerCase().replace(' ', '-')}`}
                                    >
                                        {link.name}
                                    </Link>
                                ))}
                            </nav>
                            <div className="p-6 border-t border-border">
                                <div className="flex gap-4 text-sm text-muted-foreground uppercase tracking-widest">
                                    <a href="#" className="hover:text-lime transition-colors">Instagram</a>
                                    <a href="#" className="hover:text-lime transition-colors">YouTube</a>
                                </div>
                            </div>
                        </div>
                    </SheetContent>
                </Sheet>

                {/* Logo */}
                <Link 
                    to="/" 
                    className="font-heading text-xl md:text-2xl lg:text-3xl absolute left-1/2 -translate-x-1/2 md:relative md:left-0 md:translate-x-0"
                    data-testid="logo"
                >
                    <span className="text-lime">YES</span>suhchill
                </Link>

                {/* Desktop Nav */}
                <nav className="hidden md:flex items-center gap-1 lg:gap-2">
                    {navLinks.map((link) => (
                        <Link
                            key={link.name}
                            to={link.path}
                            className={`text-[11px] lg:text-xs font-medium tracking-[0.12em] uppercase px-3 lg:px-4 py-2 transition-colors relative group ${
                                link.hot ? 'text-lime' : 'text-foreground hover:text-lime'
                            }`}
                            data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
                        >
                            {link.name}
                            <span className="absolute bottom-0 left-4 right-4 h-[2px] bg-lime transform scale-x-0 group-hover:scale-x-100 transition-transform origin-center" />
                        </Link>
                    ))}
                </nav>

                {/* Actions */}
                <div className="flex items-center gap-3 md:gap-4">
                    <button className="p-2 hover:text-lime transition-colors hidden md:block" data-testid="search-btn" onClick={() => setSearchOpen(!searchOpen)}>
                        <Search className="w-5 h-5" />
                    </button>
                    <Link to="/login" className="p-2 hover:text-lime transition-colors hidden md:block text-xs font-bold tracking-wider">LOGIN</Link>
                    <Link to="/cart" className="p-2 hover:text-lime transition-colors relative" data-testid="cart-btn">
                        <ShoppingBag className="w-5 h-5" />
                        {itemCount > 0 && (
                            <span className="absolute -top-1 -right-1 bg-lime text-black text-[10px] font-bold w-5 h-5 flex items-center justify-center" data-testid="cart-count">
                                {itemCount}
                            </span>
                        )}
                    </Link>
                </div>
            </div>
        {searchOpen && (
            <div className="absolute top-full left-0 right-0 bg-black border-b border-border px-4 py-4 z-50">
                <div className="max-w-[1400px] mx-auto flex gap-3">
                    <input
                        autoFocus
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter" && searchQuery.trim()) { navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`); setSearchOpen(false); setSearchQuery(""); }}}
                        placeholder="Search products..."
                        className="flex-1 bg-card border border-border px-4 py-3 text-sm focus:outline-none focus:border-lime"
                    />
                    <button onClick={() => { if (searchQuery.trim()) { navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`); setSearchOpen(false); setSearchQuery(""); }}} className="bg-lime text-black px-6 py-3 text-xs font-bold uppercase">SEARCH</button>
                    <button onClick={() => setSearchOpen(false)} className="p-3 hover:text-lime transition-colors"><X className="w-5 h-5" /></button>
                </div>
            </div>
        )}
        </header>
    );
};
